import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, FileDown, Pencil } from 'lucide-react'
import { observer } from 'mobx-react'
import { useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useReactToPrint } from 'react-to-print'

import logo from '@/assets/images/logo.png'
import { Button } from '@/components/elements/button'
import { Label } from '@/components/elements/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/elements/select'
import { Skeleton } from '@/components/elements/skeleton'
import { Layout } from '@/components/Layout'
import { getEAP } from '@/services/eap.service'
import { getEntityJobs } from '@/services/entity.service'
import { UserStore } from '@/stores/UserStore'

export const EAPPreview = observer(() => {
  const { entityId, eapId } = useParams()
  const navigate = useNavigate()
  const [selectedJobId, setSelectedJobId] = useState('admin')
  const printRef = useRef(null)

  const canManageEAPs =
    UserStore.isXverseAdmin || UserStore.isEntityOwner || UserStore.isEntityAdmin

  const { data: eap, isPending: eapPending } = useQuery({
    queryKey: ['accounts', 'eaps', entityId, eapId],
    queryFn: () => getEAP({ entityId, eapId }),
  })

  const { data: jobsData } = useQuery({
    queryKey: ['accounts', 'jobs', entityId],
    queryFn: () =>
      getEntityJobs({
        entityId,
        sorting: [],
        pagination: { pageSize: 100, pageIndex: 0 },
      }),
  })

  const jobs = jobsData?.results || []

  const handleExportPDF = useReactToPrint({
    contentRef: printRef,
    documentTitle: eap?.name || 'Emergency Action Plan',
  })

  if (eapPending) {
    return (
      <Layout breadcrumbs={['Admin', 'Emergency Action Plans', 'Preview']}>
        <div className="flex flex-col gap-4">
          {/* Document title and section placeholders */}
          <Skeleton className="h-10 w-1/2" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    )
  }

  // Settled without data: fetch error or a missing/deleted EAP — don't
  // masquerade as still loading.
  if (!eap) {
    return (
      <Layout breadcrumbs={['Admin', 'Emergency Action Plans', 'Preview']}>
        <p className="text-muted-foreground">
          Unable to load this Emergency Action Plan. It may have been deleted, or something went
          wrong — try refreshing.
        </p>
      </Layout>
    )
  }

  const getJobNames = (jobIds) => {
    if (!jobIds || jobIds.length === 0) return 'All jobs'
    return jobIds
      .map((id) => jobs.find((j) => j.id === id)?.title)
      .filter(Boolean)
      .join(', ')
  }

  const isVideo = (url) => {
    if (!url) return false
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv']
    return videoExtensions.some((ext) => url.toLowerCase().includes(ext))
  }

  const isImage = (url) => {
    if (!url) return false
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp']
    return imageExtensions.some((ext) => url.toLowerCase().includes(ext))
  }

  const selectedJobHasAccess =
    selectedJobId === 'admin' ||
    !selectedJobId ||
    !eap.jobs ||
    eap.jobs.length === 0 ||
    eap.jobs.some((j) => j === selectedJobId)

  const canSeeQuestion = (question) => {
    if (selectedJobId === 'admin' || !selectedJobId) return true
    if (!question.jobs || question.jobs.length === 0) return true
    return question.jobs.some((j) => j === selectedJobId)
  }

  const renderMediaContent = (file, label) => {
    if (isImage(file)) {
      return <img src={file} alt={label} className="max-h-96 w-full rounded object-contain" />
    }
    if (isVideo(file)) {
      return (
        <video src={file} controls className="max-h-96 w-full rounded">
          <track kind="captions" srcLang="en" label="English" />
          Your browser does not support the video tag.
        </video>
      )
    }
    return null
  }

  const renderQuestion = (question) => {
    if (!canSeeQuestion(question)) return null

    const hasContent = !!question.text || !!question.file

    return (
      <div key={question.id}>
        <h2 className="mb-2 text-lg text-gray-900">{question.label}</h2>
        {question.text && <p className="whitespace-pre-wrap text-gray-700">{question.text}</p>}
        {question.file && (
          <div className="mt-2 rounded border border-gray-200 bg-gray-50 p-2">
            {renderMediaContent(question.file, question.label)}
          </div>
        )}
        {!hasContent && <p className="text-sm text-gray-400 italic">No content yet</p>}
        {(selectedJobId === 'admin' || !selectedJobId) &&
          question.jobs &&
          question.jobs.length > 0 && (
            <p className="mt-2 text-xs text-gray-500">
              Available to:{' '}
              {question.jobs
                .map((jobId) => jobs.find((j) => j.id === jobId)?.title)
                .filter(Boolean)
                .join(', ')}
            </p>
          )}
      </div>
    )
  }

  const questions = eap.questions || []

  return (
    <Layout
      breadcrumbs={[
        'Admin',
        { pathname: `/accounts/${entityId}/eaps`, title: 'Emergency Action Plans' },
        'Preview',
      ]}
    >
      <div className="space-y-6">
        {/* Fixed Header */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <Button variant="ghost" onClick={() => navigate(`/accounts/${entityId}/eaps`)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Layout.Heading title={eap.name} />
          </div>
          <div className="flex shrink-0 gap-3">
            <Button variant="outline" onClick={handleExportPDF}>
              <FileDown className="mr-2 h-4 w-4" />
              Export to PDF
            </Button>
            {canManageEAPs && (
              <Button onClick={() => navigate(`/accounts/${entityId}/eaps/${eapId}`)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}
          </div>
        </div>

        {/* Job Selector — admins use this to preview what each role sees. */}
        <div className="flex items-center gap-4">
          <Label>View as:</Label>
          <Select value={selectedJobId} onValueChange={setSelectedJobId}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Admin (All Content)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Admin (All Content)</SelectItem>
              {jobs.map((job) => (
                <SelectItem key={job.id} value={job.id}>
                  {job.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* No Access Message */}
        {!selectedJobHasAccess && (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-6 text-center">
            <p className="text-yellow-800">
              This job does not have access to this Emergency Action Plan.
            </p>
          </div>
        )}

        {/* Preview Content */}
        {selectedJobHasAccess && (
          <div
            ref={printRef}
            className="overflow-hidden rounded-lg border border-gray-200 bg-white"
          >
            <style>{`
              @media print {
                video {
                  display: none !important;
                }
                img {
                  max-width: 100% !important;
                  page-break-inside: avoid;
                }
                * {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                  color-adjust: exact !important;
                }
                h2 {
                  page-break-after: avoid;
                }
              }
            `}</style>
            {/* Header Section */}
            <div className="bg-primary p-8 text-white">
              <div className="mb-2 flex items-center gap-3">
                <img src={logo} alt="RescueX" className="h-8" />
              </div>
              <h1 className="mb-2 text-3xl">{eap.name}</h1>
              <p className="text-gray-300">Emergency Action Plan</p>
              <p className="mt-1 text-sm text-gray-400">
                {selectedJobId === 'admin' || !selectedJobId
                  ? 'All Jobs'
                  : jobs.find((j) => j.id === selectedJobId)?.title || 'Unknown Job'}
              </p>
            </div>

            {/* Content Section */}
            <div className="space-y-8 p-8">
              {eap.description && (
                <div>
                  <h2 className="mb-2 text-lg text-gray-900">Description</h2>
                  <p className="text-gray-700">{eap.description}</p>
                </div>
              )}

              {(selectedJobId === 'admin' || !selectedJobId) && (
                <div>
                  <h2 className="mb-2 text-lg text-gray-900">
                    Jobs that can access this emergency action plan
                  </h2>
                  <p className="text-gray-700">{getJobNames(eap.jobs)}</p>
                </div>
              )}

              {questions.map(renderQuestion)}

              <div className="border-t border-gray-200 pt-8 text-sm text-gray-600">
                <div className="flex justify-between">
                  <div>
                    {eap.createdAt && (
                      <p>
                        Created:{' '}
                        {new Date(eap.createdAt).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                    )}
                    {eap.modifiedAt && (
                      <p>
                        Last Updated:{' '}
                        {new Date(eap.modifiedAt).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
})
